import { fadeDown } from "../config/animations";
import { motion } from "framer-motion";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControlLabel,
  Stack,
  Switch,
} from "@mui/material";
import { useState } from "react";

const states = ["Punjab", "Haryana", "Delhi", "Maharashtra", "Karnataka"];
const experienceLevels = [
  "No Experience",
  "6–12 Months",
  "1–2 Years",
  "3+ Years",
];

const HiringForm = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    zip: "",
    state: "",
    experience: "",
    consent: false,
  });

  const isFormValid =
    formData.firstName &&
    formData.lastName &&
    formData.email &&
    formData.phone &&
    formData.zip &&
    formData.state &&
    formData.experience &&
    formData.consent;

  const handleChange = (field) => (e) => {
    const value = field === "consent" ? e.target.checked : e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Box
      sx={{
        backgroundImage:
          "url('https://www.shutterstock.com/image-photo/blurred-modern-office-soft-lighting-600nw-2591077855.jpg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        minHeight: "100vh",
        display: "flex",
        flexDirection: { xs: "column", lg: "row" },
        justifyContent: "space-evenly",
        alignItems: "center",
        position: "relative",
        py: 10,
        px: { xs: 2, sm: 4, md: 6 },
      }}
    >
      {/* Dark overlay */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          zIndex: 1,
        }}
      />

      <Box
        sx={{
          width: { xs: "100%", lg: "40%" },
          maxWidth: 900,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          px: { xs: 0, sm: 3, md: 4 },
          zIndex: 2,
        }}
      >
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.6 }}
          variants={fadeDown}
        >
          <Typography
            sx={{
              fontSize: {
                xs: "1.5rem",
                sm: "2.5rem",
                md: "2.5rem",
                lg: "2.5rem",
              },
              color: "white",
              //textShadow: "0 2px 16px #000",
              fontWeight: 700,
              lineHeight: 1.1,
              wordWrap: "break-word",
            }}
          >
            Rewarding Truck Driving Jobs
          </Typography>
          <Typography
            variant="body2"
            fontSize={{
              xs: "1rem",
              sm: "1.2rem",
              md: "1.3rem",
              lg: "1.1rem",
            }}
            color="#fff"
            maxWidth={700}
            mx="auto"
            px={{ xs: 0, sm: 1, md: 0 }}
            sx={{
              textShadow: "0 1px 8px #000",
              mt: 2,
              fontWeight: 400,
              display: { xs: "none", md: "flex" },
            }}
          >
            Join a team that keeps the world moving. At FlyHi, we offer
            rewarding truck driving careers with hands-on training, competitive
            pay, and a commitment to your growth. Whether you're just starting
            out or bringing years of experience, we treat every driver like
            family — because every mile matters.
          </Typography>
        </motion.div>
      </Box>

      {/* Form container */}
      <Box
        sx={{
          zIndex: 2,
          backgroundColor: "rgba(255, 255, 255, 0.75)",
          borderRadius: 10,
          p: 3,
          py: { xs: 6, lg: 3 },
          maxWidth: { xs: "100%", lg: "30%" },
          width: "100%",
          boxShadow: 6,
        }}
      >
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Apply to Drive
        </Typography>

        <Stack spacing={2}>
          {/* First Name & Last Name */}
          <Stack direction="row" spacing={2}>
            <TextField
              label="First Name"
              fullWidth
              value={formData.firstName}
              onChange={handleChange("firstName")}
            />
            <TextField
              label="Last Name"
              fullWidth
              value={formData.lastName}
              onChange={handleChange("lastName")}
            />
          </Stack>

          {/* Email */}
          <TextField
            label="Email"
            type="email"
            fullWidth
            value={formData.email}
            onChange={handleChange("email")}
          />

          {/* Country Code & Phone Number */}
          <Stack direction="row" spacing={2}>
            <TextField
              value="+1"
              disabled
              sx={{
                width: "15%",
                //backgroundColor: "#f5f5f5",
              }}
            />
            <TextField
              label="Phone Number"
              type="tel"
              sx={{ width: "85%" }}
              value={formData.phone}
              onChange={handleChange("phone")}
              InputProps={{
                inputProps: { pattern: "[0-9]{10}" },
              }}
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              select
              label="Select State"
              sx={{
                width: "60%",
                //backgroundColor: "#f5f5f5",
              }}
              value={formData.state}
              onChange={handleChange("state")}
            >
              {states.map((state) => (
                <MenuItem key={state} value={state}>
                  {state}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Zip Code"
              sx={{ width: "40%" }}
              value={formData.zip}
              onChange={handleChange("zip")}
              InputProps={{
                inputProps: { pattern: "[0-9]{6}" },
              }}
            />
          </Stack>

          {/* Experience */}
          <TextField
            select
            label="Driving Experience"
            value={formData.experience}
            onChange={handleChange("experience")}
            fullWidth
          >
            {experienceLevels.map((level) => (
              <MenuItem key={level} value={level}>
                {level}
              </MenuItem>
            ))}
          </TextField>

          {/* Consent */}
          <FormControlLabel
            control={
              <Switch
                checked={formData.consent}
                onChange={handleChange("consent")}
              />
            }
            label="I agree to be contacted by FlyHi about job and training opportunities."
          />

          {/* Buttons */}
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button variant="contained" color="primary" disabled={!isFormValid}>
              Submit
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                window.location.href = "tel:+918329409000"; // replace with recruiter’s phone number
              }}
            >
              Call a Recruiter
            </Button>
            {/* <Button variant="text" color="primary">
              Full Application
            </Button> */}
          </Stack>
          <Typography
            variant="body2"
            fontSize={{
              xs: "1rem",
              sm: "1.2rem",
              md: "1.3rem",
              lg: "1.1rem",
            }}
            color="#000000ff"
            maxWidth={700}
            mx="auto"
            px={{ xs: 0, sm: 1, md: 0 }}
            sx={{
              mt: 2,
              fontWeight: 400,
              display: { xs: "block", md: "none" },
            }}
          >
            Join a team that keeps the world moving.
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

export default HiringForm;
